import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { LessonContentSection } from '@/components/LessonContentSection';
import { useLessonBundle } from '@/hooks/useLessonBundle';

type Props = {
  lessonId: string;
  onExit: () => void;
  onCompleted?: () => void;
};

/**
 * A coach-authored lesson: instructions plus the coach's ordered content.
 * There is no quiz — the lesson is complete once every required item is done
 * (or, for an empty lesson, once the player marks it complete).
 */
export function CustomLessonView({ lessonId, onExit, onCompleted }: Props) {
  const { bundle, loading, reload } = useLessonBundle(lessonId);
  const [saving, setSaving] = useState(false);

  const custom = (bundle as any).custom_lesson as
    | { title: string; description: string | null }
    | null
    | undefined;
  const items = bundle.items ?? [];
  const requiredPending = Math.max(0, bundle.required_total - bundle.required_done);
  const canComplete = requiredPending === 0;
  const alreadyDone = bundle.quiz_complete;

  const markComplete = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('record_lesson_completion' as any, {
        p_lesson_id: lessonId,
        p_correct: 0,
        p_total: 0,
        p_xp_reward: 10,
        p_question_results: [],
      });
      if (error) throw error;
      toast.success('Lesson complete! +10 XP');
      await reload();
      onCompleted?.();
    } catch {
      toast.error('Could not save completion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 pt-4 pb-28">
      <button onClick={onExit} className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {loading ? (
        <div className="h-24 rounded-2xl bg-white/5 animate-pulse" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="bg-card rounded-2xl border-2 border-border p-4 shadow-card">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="w-4 h-4 text-primary" />
              <span className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground">
                From your coach
              </span>
            </div>
            <h1 className="font-display font-bold text-xl mb-2">{custom?.title ?? 'Lesson'}</h1>
            {custom?.description ? (
              <p className="text-sm font-body text-foreground/80 whitespace-pre-wrap">{custom.description}</p>
            ) : (
              <p className="text-sm font-body text-muted-foreground">Your coach didn't add instructions for this one.</p>
            )}
          </div>

          <LessonContentSection lessonId={lessonId} onProgress={() => reload()} />

          {items.length === 0 && (
            <p className="text-xs font-body text-muted-foreground text-center">
              This lesson has no content yet — your coach may add some later.
            </p>
          )}

          <div className="bg-card rounded-2xl border-2 border-border p-4 shadow-card space-y-2">
            {alreadyDone ? (
              <p className="flex items-center justify-center gap-2 text-sm font-display font-bold text-emerald-400">
                <CheckCircle2 className="w-4 h-4" /> Lesson completed
              </p>
            ) : (
              <>
                {!canComplete && (
                  <p className="text-xs font-body text-muted-foreground text-center">
                    Finish {requiredPending} required item{requiredPending === 1 ? '' : 's'} to complete this lesson.
                  </p>
                )}
                <Button
                  className="w-full font-display font-bold"
                  disabled={!canComplete || saving}
                  onClick={markComplete}
                >
                  {saving ? 'Saving…' : 'Mark lesson complete'}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
